import { t } from "@lingui/macro";
import {
  ArrowSquareOut,
  BookmarkSimple,
  Brain,
  Briefcase,
  CaretRight,
  ChatsCircle,
  House,
  Kanban,
  MagnifyingGlass,
  Robot,
  SidebarSimpleIcon,
  Star,
  X as XIcon,
  Target,
} from "@phosphor-icons/react";
import { cn } from "@stylingcv/utils";
import { LocalizedLink as Link } from "@/client/components/localized-link";
import useKeyboardShortcut from "use-keyboard-shortcut";

import { Logo } from "@/client/components/logo";
import { Loader } from "@/client/components/ui/loader";
import { UserProfileFooter } from "@/client/components/user-profile-footer";
import { useUser } from "@/client/services/user";
import { useAuthStore } from "@/client/stores/auth";
import { useFeatureSettings } from "@/client/hooks/use-feature-settings";

import { useLocalizedNavigation } from "../../../hooks/useLocalizedNavigation";

type SidebarItemProps = {
  path: string;
  name: string;
  icon: React.ReactNode;
  onClick?: () => void;
  collapsed?: boolean;
  comingSoon?: boolean;
  badge?: string;
  badgeColor?: string;
  isRTL?: boolean;
  isMobileSheet?: boolean;
};

const SidebarItem = ({
  path,
  name,
  icon,
  onClick,
  collapsed,
  comingSoon,
  badge,
  badgeColor,
  isRTL = false,
  isMobileSheet = false,
}: SidebarItemProps) => {
  const { getLocalizedHref, getCurrentPath } = useLocalizedNavigation();
  const currentPathWithoutLocale = getCurrentPath();
  const isActive = currentPathWithoutLocale === path;

  const content = (
    <div
      className={cn(
        "group/item relative flex w-full items-center gap-2.5 rounded-xl py-1.5 text-[13px] font-medium transition-all duration-200",
        collapsed ? "justify-center px-2" : "px-3",
        isMobileSheet && !collapsed && "py-2",
        isRTL && !collapsed && "text-right",
        comingSoon
          ? "cursor-not-allowed text-muted-foreground/70"
          : cn(
              "text-muted-foreground hover:bg-black/[0.04] hover:text-[var(--stylion-ink)]",
              isActive && "bg-primary/10 font-semibold text-primary",
            ),
      )}
    >
      {/* Active indicator */}
      {(isActive || !comingSoon) && (
        <div className={cn(
          "absolute inset-y-0 start-0 w-[3px] rounded-e-sm bg-primary transition-transform duration-300",
          isActive ? "scale-y-100" : "scale-y-0 group-hover/item:scale-y-100"
        )} />
      )}

      <span className={cn(
        "flex size-8 shrink-0 items-center justify-center rounded-lg transition-all duration-200",
        comingSoon
          ? "text-gray-400"
          : cn(
              isActive ? "text-primary" : "text-muted-foreground group-hover/item:text-[var(--stylion-ink)]"
            )
      )}>
        {icon}
      </span>

      {!collapsed && (
        <>
          <span className={cn("min-w-0 flex-1 truncate", isRTL && "text-right")}>
            {name}
          </span>
          {comingSoon && (
            <span className="shrink-0 rounded-full border border-primary/25 bg-primary/10 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-primary">
              {t`Soon`}
            </span>
          )}
          {badge && !comingSoon && (
            <span className={cn("shrink-0 rounded-md px-1.5 py-0.5 text-xs font-medium", badgeColor)}>
              {badge}
            </span>
          )}
          {!comingSoon && !badge && (
            <CaretRight
              size={14}
              className="shrink-0 text-muted-foreground/50 opacity-0 transition-all duration-200 group-hover/item:translate-x-0.5 group-hover/item:text-primary group-hover/item:opacity-100 rtl:rotate-180 rtl:group-hover/item:-translate-x-0.5"
            />
          )}
        </>
      )}
    </div>
  );

  if (comingSoon) {
    return content;
  }

  return (
    <Link
      to={getLocalizedHref(path)}
      onClick={onClick}
      title={collapsed ? name : undefined}
      className="block w-full"
    >
      {content}
    </Link>
  );
};

type SidebarProps = {
  setOpen?: (open: boolean) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
};

export const Sidebar = ({ setOpen, collapsed = false, onToggleCollapse }: SidebarProps) => {
  const { user, loading: userLoading } = useUser();
  const subscriptionTier = useAuthStore((state) => state.subscriptionTier);
  const { navigateLocalized } = useLocalizedNavigation();
  const { jobTracker } = useFeatureSettings();
  const isRTL = document.documentElement.dir === "rtl";
  const isMobileSheet = Boolean(setOpen);
  const sidebarCollapsed = isMobileSheet ? false : collapsed;

  useKeyboardShortcut(["shift", "r"], () => {
    navigateLocalized("/dashboard/resumes");
    setOpen?.(false);
  });

  useKeyboardShortcut(["shift", "s"], () => {
    navigateLocalized("/dashboard/settings");
    setOpen?.(false);
  });

  useKeyboardShortcut(["shift", "g"], () => {
    navigateLocalized("/dashboard/smart-generator");
    setOpen?.(false);
  });

  useKeyboardShortcut(["shift", "m"], () => {
    navigateLocalized("/pricing");
    setOpen?.(false);
  });

  if (userLoading) {
    return (
      <div className="stylion-app-shell flex h-full flex-col items-center justify-center border-r">
        <Loader className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  // Main navigation items
  const mainNavItems = [
    { path: "/dashboard/resumes", name: t`Dashboard`, icon: <House size={18} weight="duotone" />, enabled: true },
    { path: "/dashboard/job-search", name: t`Job Search`, icon: <MagnifyingGlass size={18} weight="duotone" />, enabled: true },
    { path: "/dashboard/job-tracker", name: t`Job Tracker`, icon: <Kanban size={18} weight="duotone" />, enabled: true },
    { path: "/dashboard/smart-generator", name: t`AI Generator`, icon: <Robot size={18} weight="duotone" />, enabled: true },
    { path: "/dashboard/interview", name: t`Interview`, icon: <Briefcase size={18} weight="duotone" />, enabled: true },
    { path: "/dashboard/interview/voice", name: t`Voice Interview`, icon: <ChatsCircle size={18} weight="duotone" />, enabled: true },
    { path: "/dashboard/ai-assistant", name: t`AI Assistant`, icon: <Brain size={18} weight="duotone" />, enabled: false },
    { path: "/ats-score-checker", name: t`ATS Checker`, icon: <Target size={18} weight="duotone" />, enabled: true },
  ].filter(item => item.enabled);

  return (
    <div
      dir={document.documentElement.dir || "ltr"}
      className="stylion-app-shell relative flex h-full w-full flex-col border-r text-[var(--stylion-ink)]"
    >
      {/* Header with Logo and Toggle */}
      <div className={cn(
        "relative z-10 flex shrink-0 items-center border-b border-black/5 pb-4 pt-5",
        sidebarCollapsed ? "justify-center px-2" : "justify-between px-4"
      )}>
        {!sidebarCollapsed && (
          <Link to="/" className="flex items-center px-1">
            <Logo size={140} className="h-9 w-auto max-w-[140px]" />
          </Link>
        )}
        {setOpen ? (
          <button
            onClick={() => setOpen(false)}
            className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-all duration-200 hover:bg-black/5 hover:text-[var(--stylion-ink)]"
            aria-label="Close menu"
          >
            <XIcon size={20} weight="bold" />
          </button>
        ) : (
          <button
            onClick={onToggleCollapse}
            className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-all duration-200 hover:bg-black/5 hover:text-[var(--stylion-ink)]"
            aria-label="Toggle sidebar"
          >
            <SidebarSimpleIcon size={18} weight="regular" />
          </button>
        )}
      </div>

      {/* Navigation Area - Scrollable */}
      <style>{`
  .brand-sidebar-scroll::-webkit-scrollbar {
    width: 6px;
  }
  .brand-sidebar-scroll::-webkit-scrollbar-track {
    background: transparent;
  }
  .brand-sidebar-scroll::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 10px;
  }
  .brand-sidebar-scroll::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.2);
  }
`}</style>
      <div className="brand-sidebar-scroll flex flex-1 flex-col overflow-y-auto overflow-x-hidden px-2 py-3">
        {/* Main Navigation */}
        {!sidebarCollapsed && (
          <div className={cn("mb-1 px-3", isRTL && "text-right")}>
            <span className="font-sans text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t`Menu`}
            </span>
          </div>
        )}
        <nav aria-label={t`Main navigation`} className="flex flex-col gap-0.5">
          {mainNavItems.map((item) => (
            <SidebarItem
              key={item.path}
              {...item}
              collapsed={sidebarCollapsed}
              isRTL={isRTL}
              isMobileSheet={isMobileSheet}
              onClick={() => setOpen?.(false)}
            />
          ))}
        </nav>

        {/* Billing Section */}
        <div className="mt-1">
          {!sidebarCollapsed && (
            <div className={cn("mb-0.5 px-3", isRTL && "text-right")}>
              <span className="font-sans text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t`Billing`}
              </span>
            </div>
          )}
          <SidebarItem 
            path="/pricing" 
            name={t`Pricing`} 
            icon={<BookmarkSimple size={18} weight="duotone" />} 
            collapsed={sidebarCollapsed} 
            isRTL={isRTL}
            isMobileSheet={isMobileSheet}
            onClick={() => setOpen?.(false)} 
          />
        </div>

        {/* Version 1 Legacy App */}
        <div className="mt-1">
          {!sidebarCollapsed && (
            <div className={cn("mb-0.5 px-3", isRTL && "text-right")}>
              <span className="font-sans text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t`Legacy`}
              </span>
            </div>
          )}
          <a
            href="https://app.stylion.io"
            target="_blank"
            rel="noopener noreferrer"
            title={sidebarCollapsed ? t`Classic Resume Builder` : undefined}
            className="block w-full"
          >
            <div
              className={cn(
                "group/item relative flex w-full items-center gap-2.5 rounded-xl py-2 text-[13px] font-medium transition-all duration-200",
                sidebarCollapsed ? "justify-center px-2" : "px-3",
                "text-muted-foreground hover:bg-black/[0.04] hover:text-[var(--stylion-ink)]"
              )}
            >
              <span className={cn(
                "flex size-8 shrink-0 items-center justify-center rounded-lg transition-all duration-200",
                "text-muted-foreground group-hover/item:text-primary"
              )}>
                <ArrowSquareOut size={18} weight="duotone" />
              </span>

              {!sidebarCollapsed && (
                <>
                  <span className={cn("min-w-0 flex-1 truncate", isRTL && "text-right")}>{t`Version 1 Builder`}</span>
                  <span className="shrink-0 rounded-md bg-amber-100/80 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-600 dark:bg-amber-900/30 dark:text-amber-400">
                    {t`Classic`}
                  </span>
                </>
              )}
            </div>
          </a>
        </div>

        {/* Plan Card - scrolls with the navigation */}
        {!sidebarCollapsed && (
          <Link
            to="/pricing"
            onClick={() => setOpen?.(false)}
            className="stylion-app-panel group mx-1 mt-4 flex min-h-[58px] items-center gap-2.5 px-3 py-2.5 transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_30px_hsl(199_87%_48%_/_0.08)]"
          >
            <span className="flex size-9 shrink-0 items-center justify-center rounded-[12px] bg-[var(--stylion-cta-from)] text-[var(--stylion-ink)] ring-1 ring-black/5 transition group-hover:bg-primary group-hover:text-primary-foreground">
              <Star size={16} weight="duotone" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-bold leading-tight text-[var(--stylion-ink)]">
                {subscriptionTier === "BASIC" ? t`Basic plan`
                  : subscriptionTier === "PROFESSIONAL" ? t`Pro plan`
                  : subscriptionTier === "ENTERPRISE" ? t`Enterprise plan`
                  : t`Free plan`}
              </span>
              <span className="mt-0.5 block truncate text-[10.5px] font-medium leading-tight text-gray-400">
                {t`AI rewrites and billing`}
              </span>
            </span>
            <span className="shrink-0 rounded-full bg-primary px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-primary-foreground shadow-[var(--glow-primary)] transition group-hover:opacity-90">
              {t`Manage`}
            </span>
          </Link>
        )}

        {/* Spacer */}
        <div className="min-h-8 flex-1" />
      </div>

      {/* User Profile Section - Fixed at bottom */}
      <UserProfileFooter collapsed={sidebarCollapsed} />
    </div>
  );
};
